import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingCard,
  Skeleton,
} from '@folio/components';
import { useInvoicingFrequencyLabels } from '@folio/constants';
import { cn, formatNumber } from '@folio/lib';
import { useKorintProductConfig } from 'korint-sdk/react';
import { AlertCircleIcon, CircleCheckBig } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { keyValueFieldsToObject, type CalculatePolicyQuoteOutput } from 'korint-sdk';
import type { AvailablePricingTier, PriceBreakdown } from '../shared-quote/use-available-pricing';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Alert, AlertDescription } from '@folio/components/ui/alert';

type PricingTierSelectProps = {
  tiers: AvailablePricingTier[];
  selectedTierPricing?: CalculatePolicyQuoteOutput & { breakdown?: PriceBreakdown };
  productId: string;
  isLoading?: boolean;
  isPricingLoading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
};

export function PricingTierSelect({
  tiers,
  productId,
  isLoading,
  isPricingLoading,
  disabled,
  readOnly,
}: Readonly<PricingTierSelectProps>) {
  const { t } = useTranslation();
  const form = useFormContext();
  const { productsConfig } = useKorintProductConfig();
  const invoicingFrequencyLabels = useInvoicingFrequencyLabels();

  const { quoteTierSelectModuleConfig } = productsConfig?.[productId] || {};

  const currentTier = form.watch('tier');
  const invoicingFrequency = form.watch('invoicingConfig.frequency');
  const tierError = form.formState.errors.tier;
  const effectiveFrequency = invoicingFrequency ?? tiers[0]?.breakdown?.frequency;

  if (isLoading) {
    return <LoadingCard />;
  }

  const displayedTiers = readOnly ? tiers.filter(t => t.tierId === currentTier) : tiers;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div>
            {readOnly ? t('pricingTierSelect.readOnlyTitle') : t('pricingTierSelect.title')}
          </div>
          {!readOnly && (
            <div className="text-sm text-gray-600">{t('pricingTierSelect.description')}</div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'grid gap-4',
            displayedTiers.length === 1 && 'lg:mx-auto lg:w-2/5',
            displayedTiers.length === 2 && 'lg:grid-cols-2',
            displayedTiers.length === 3 && 'lg:grid-cols-3',
            displayedTiers.length === 4 && 'lg:grid-cols-4',
            displayedTiers.length > 4 && 'lg:grid-cols-4',
          )}
        >
          {displayedTiers.length === 0 && (
            <Alert variant="warning">
              <AlertCircleIcon />
              <AlertDescription>{t('pricingTierSelect.noTiers')}</AlertDescription>
            </Alert>
          )}
          {displayedTiers.map(tier => {
            const isSelected = currentTier === tier.tierId;
            const breakdown = tier.breakdown;

            const total = breakdown?.recurring?.withFeesAndTaxes;

            const configuredOptionalPerilIds = quoteTierSelectModuleConfig?.optionalPerilIds ?? [];
            const includedPerils = tier.perils.filter(
              p => !configuredOptionalPerilIds.includes(p.id),
            );
            const optionalPerils = tier.perils.filter(p =>
              configuredOptionalPerilIds.includes(p.id),
            );

            const tierFieldValues = Object.fromEntries(
              tier.perils.flatMap(p =>
                (p.defaultCustomFieldValues ?? []).map(({ key, value }) => [key, value]),
              ),
            );

            const visibleOptionalPerils = optionalPerils.flatMap(p => {
              if (!p.included) return [];
              const rawDefault = (p.defaultCustomFieldValues ?? [])[0]?.value ?? p.options?.[0];
              const customFieldName = p.customFields?.[0];
              const effectiveValue =
                typeof rawDefault === 'boolean'
                  ? p.included
                  : ((customFieldName ? form.watch(customFieldName) : undefined) ?? rawDefault);
              const value =
                effectiveValue != null
                  ? t([
                      `${productId}.perils.${p.id}.options.${effectiveValue}`,
                      `options.${effectiveValue}`,
                      String(effectiveValue),
                    ])
                  : undefined;
              return [{ p, value }];
            });

            const hasVisiblePerils = includedPerils.length > 0 || visibleOptionalPerils.length > 0;

            return (
              <Stack
                key={tier.tierId}
                className={cn(
                  'rounded-2xl border-2 p-6 gap-5 transition-all duration-200',
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border bg-card hover:border-primary/40 hover:shadow-sm',
                )}
              >
                <Stack gap={1}>
                  <div className="text-lg font-bold">
                    {t(
                      [`${productId}.tiers.${tier.tierId}.label`, `tiers.${tier.tierId}.label`],
                      tierFieldValues,
                    )}
                  </div>
                  {Boolean(
                    t([
                      `${productId}.tiers.${tier.tierId}.description`,
                      `tiers.${tier.tierId}.description`,
                      'empty',
                    ]),
                  ) && (
                    <div className="text-sm text-muted-foreground">
                      {t([
                        `${productId}.tiers.${tier.tierId}.description`,
                        `tiers.${tier.tierId}.description`,
                      ])}
                    </div>
                  )}
                </Stack>

                <Group align="baseline" gap={0} className="gap-1.5">
                  <Text inline weight="bold" className="text-4xl">
                    {isPricingLoading || total === undefined ? (
                      <Skeleton className="w-24 h-9 inline-flex" />
                    ) : (
                      `${formatNumber(total / 100)} €`
                    )}
                  </Text>
                  {effectiveFrequency in invoicingFrequencyLabels && (
                    <Text inline size="sm" tone="muted">
                      / {invoicingFrequencyLabels[effectiveFrequency]}
                    </Text>
                  )}
                </Group>

                {quoteTierSelectModuleConfig?.customIncludedPerils ? (
                  <div
                    className={cn(
                      'grow rounded-xl p-3 transition-colors duration-200',
                      isSelected ? 'bg-primary/10' : 'bg-muted/50',
                    )}
                  >
                    {quoteTierSelectModuleConfig.customIncludedPerils({
                      tier: tier.tierId,
                      includedPerils: includedPerils.map(p => ({ id: p.id })),
                    })}
                  </div>
                ) : (
                  hasVisiblePerils && (
                    <div
                      className={cn(
                        'grow rounded-xl p-3 transition-colors duration-200',
                        isSelected ? 'bg-primary/10' : 'bg-muted/50',
                      )}
                    >
                      {includedPerils.length > 0 && (
                        <div className="mb-2 font-bold text-sm">
                          {t('pricingTierSelect.includedPerils')}
                        </div>
                      )}
                      <ul className="text-sm flex flex-col gap-1">
                        {includedPerils.map(p => {
                          const defaults = p.defaultCustomFieldValues ?? [];
                          const defaultsMap = keyValueFieldsToObject(
                            defaults as Array<{ key: string; value: string | number | boolean }>,
                          );
                          const rawValue = defaults[0]?.value ?? p.options?.[0];
                          const value =
                            rawValue != null
                              ? t([
                                  `${productId}.perils.${p.id}.options.${rawValue}`,
                                  `options.${rawValue}`,
                                  String(rawValue),
                                ])
                              : undefined;
                          return (
                            <li key={p.id} className="flex items-start gap-2">
                              <CircleCheckBig className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
                              <span>
                                {t(`${productId}.perils.${p.id}.label`, {
                                  ...defaultsMap,
                                  value,
                                  count: Number(rawValue) || 0,
                                })}
                              </span>
                            </li>
                          );
                        })}
                        {visibleOptionalPerils.map(({ p, value }) => (
                          <li key={p.id} className="flex items-start gap-2">
                            <CircleCheckBig className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
                            <span>
                              {t(`${productId}.perils.${p.id}.tierLabel`, {
                                context: String(p.included),
                                defaultValue: t(`${productId}.perils.${p.id}.label`),
                                value,
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}

                {!readOnly && (
                  <Button
                    variant={isSelected ? 'default' : 'outline'}
                    className="w-full"
                    disabled={isPricingLoading || disabled}
                    onClick={() => {
                      if (!isSelected) {
                        form.setValue('tier', tier.tierId, { shouldDirty: true });
                      }
                    }}
                  >
                    {isSelected ? t('pricingTierSelect.selected') : t('pricingTierSelect.choose')}
                  </Button>
                )}
              </Stack>
            );
          })}
        </div>
        {tierError && (
          <Text size="sm" tone="destructive" className="mt-2">
            {typeof tierError.message === 'string' ? tierError.message : ''}
          </Text>
        )}
      </CardContent>
    </Card>
  );
}
